/**
 * Autocomplete de cidades usando a API pública do IBGE.
 * - Ao selecionar uma UF, carrega as cidades daquele estado (cache local).
 * - Enquanto o usuário digita, mostra as cidades cujo nome começa com as letras digitadas.
 * - Cache em memória por UF (evita chamadas repetidas).
 * - Cai graciosamente para input livre caso a API/rede falhe.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";

// Cache global (persistente durante a sessão)
const CITY_CACHE = {};

async function fetchCitiesByUF(uf) {
  if (!uf) return [];
  if (CITY_CACHE[uf]) return CITY_CACHE[uf];
  try {
    const res = await fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios`);
    if (!res.ok) return [];
    const data = await res.json();
    const cities = data.map((c) => c.nome).sort((a, b) => a.localeCompare(b, "pt-BR"));
    CITY_CACHE[uf] = cities;
    return cities;
  } catch {
    return [];
  }
}

const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export function CityAutocomplete({ value, onChange, uf, placeholder = "Cidade", "data-testid": testId, className = "" }) {
  const [cities, setCities] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    let alive = true;
    fetchCitiesByUF(uf).then((list) => { if (alive) setCities(list); });
    return () => { alive = false; };
  }, [uf]);

  const suggestions = useMemo(() => {
    if (!value || value.length < 2) return [];
    const q = norm(value);
    return cities.filter((c) => norm(c).startsWith(q)).slice(0, 8);
  }, [value, cities]);

  useEffect(() => {
    const h = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const pick = (city) => {
    onChange({ target: { value: city } });
    setOpen(false);
    setHighlight(-1);
    inputRef.current?.blur();
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && highlight >= 0) { e.preventDefault(); pick(suggestions[highlight]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <Input
        ref={inputRef}
        value={value || ""}
        onChange={(e) => { onChange(e); setOpen(true); setHighlight(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={uf ? placeholder : "⚠️ Selecione a UF antes"}
        disabled={!uf}
        autoComplete="off"
        data-testid={testId}
        className={!uf ? "bg-amber-50 border-amber-200 placeholder:text-amber-700" : ""}
      />
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-56 overflow-y-auto text-sm">
          {suggestions.map((c, i) => (
            <button
              type="button"
              key={c}
              onClick={() => pick(c)}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full text-left px-3 py-2 hover:bg-blue-50 ${i === highlight ? "bg-blue-100 font-semibold" : ""}`}
              data-testid={`${testId}-suggestion-${i}`}
            >
              {c}
            </button>
          ))}
        </div>
      )}
      {open && value && value.length >= 2 && suggestions.length === 0 && cities.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-amber-200 rounded-md shadow-md p-2 text-xs text-amber-700">
          Nenhuma cidade encontrada em {uf} com "{value}". Você pode digitar livre.
        </div>
      )}
    </div>
  );
}
